import { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { palette } from 'styled-tools';

import { fontSize, FontSizes, minWidth, OptionalBackgroundButton } from '@calm-web/design-system';

import { messages as confMessages } from '@/components/premiumConfirmation/data';
import { StoreButton } from '@/components/ui/StoreButton';
// import AppStoreButtons from '@/components/AppStoreButtons';
// import colors from '@/components/redesign/Colors';
// import TrackedButton from '@/components/redesign/TrackedButton';
// import { generatePartnerUrl } from '@/utils/urlUtils';
import { useAnalytics } from '@/hooks/analytics';
import { useUserState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { generatePartnerUrl } from '@/utils/generatePartnerUrl';

// TODO: Check the appstorebuttons on every other page

const Wrapper = styled.div`
	box-sizing: border-box;
	max-width: 464px;
	width: 90%;

	${minWidth('desktop')} {
		max-width: none;
		padding: 0 1rem 0 4.88rem;
		width: 100%;
	}
`;

const SubmitButton = styled(OptionalBackgroundButton)`
	border-radius: 36px;
	${fontSize(FontSizes.lg)};
	font-weight: 900;
	height: 72px;
	background: linear-gradient(135deg, #75b2f0 0%, #b275f0 100%);
	width: 100%;
`;

const StyledLink = styled.a`
	color: ${palette('blue3')};

	&:hover {
		text-decoration: underline;
	}
`;

interface RedeemSuccessProps {
	giftType?: string;
	isLifetime: boolean;
}

const messages = defineMessages({
	successTitle: {
		id: 'redeem.redeemSuccess.default.message1',
		defaultMessage: 'Congrats{name}! Your subscription to Calm has been activated.',
		description:
			// eslint-disable-next-line max-len
			'Headline for the text we show them when they successfully redeem a gift card code. {name} is either empty, or has a comma before their name (e.g., either "Congrats!" or "Congrats, Jane!")',
	},
	successBody: {
		id: 'redeem.redeemSuccess.default.message2',
		defaultMessage:
			// eslint-disable-next-line max-len
			'Download the App for iOS or Android to have Calm in your pocket and at your fingertips whenever you might need it. You can also use Calm right here in your browser too!',
		description: 'Instructions for how to use the app now that they have a subscription',
	},
	successBodyTwo: {
		id: 'redeem.redeemSuccess.default.message3',
		defaultMessage:
			// eslint-disable-next-line max-len
			'We recommend starting with the Daily Calm sessions we release each day. We also have Sleep Stories, meditation for kids, and a variety of different types of content to choose from.',
		description: 'Instructions for how to use the app now that they have a subscription',
	},
	openApp: {
		id: 'redeem.redeemSuccess.default.startCalmButton',
		defaultMessage: 'Start Calm Now',
		description: 'Button to launch the Calm app (either on their phone or in their browser)',
	},
	teacherSuccessTitle: {
		id: 'redeem.redeemSuccess.teacher.message1',
		defaultMessage:
			'Congrats{name}! Your <bold>free classroom subscription</bold> to Calm has been activated.',
		description:
			// eslint-disable-next-line max-len
			'Headline for the text we show them when they successfully redeem a gift card code. {name} is either empty, or has a comma before their name (e.g., either "Congrats!" or "Congrats, Jane!")',
	},
	teacherSuccessBody: {
		id: 'redeem.redeemSuccess.teacher.message2',
		defaultMessage:
			// eslint-disable-next-line max-len
			'We hope your students benefit from learning mindfulness. Click <teacherlink>here</teacherlink> to read our onboarding document designed specifically for Calm Schools. And let us know how it all goes.',
		description: 'Instructions for how to use the app in a school setting now that they have a subscription',
	},
	teacherContactUs: {
		id: 'redeem.redeemSuccess.teacher.contactUs',
		defaultMessage: 'If you have any questions contact us <contactlink>here</contactlink>',
		description: 'Teachers can reach out to our support team for help',
	},
});

const RedeemSuccess = ({ giftType, isLifetime }: RedeemSuccessProps) => {
	const { logEvent } = useAnalytics();
	const user = useUserState();
	const { formatMessage } = useIntl();
	const routerPush = useRouterPush();

	useComponentDidMount(() => {
		logEvent({ eventName: 'Gift Redeem : Success : Landed' });
	});

	const onClickOnboardingDoc = () => {
		logEvent({ eventName: 'Gift Redeem : Success : Onboarding Doc : Clicked' });
	};

	const firstNameString = () => {
		return user?.name ? `, ${user?.name}` : '';
	};

	const defaultBody = () => {
		// TODO: Get the actual subscription duration (as soon as api dev is back up to test it)
		return (
			<div className="body">
				<p>
					{formatMessage(messages.successTitle, {
						name: firstNameString(),
					})}
				</p>
				<br />
				{isLifetime && (
					<>
						<p>
							{formatMessage(confMessages.lifetimeCancelCTA, {
								cancellink: (chunks: ReactNode) => (
									<StyledLink
										href="https://calmhelp.zendesk.com/hc/en-us/articles/360037871774"
										target="_blank"
										rel="noopener noreferrer"
									>
										{chunks}
									</StyledLink>
								),
							})}
						</p>
						<br />
					</>
				)}
				<p>{formatMessage(messages.successBody)}</p>
				<br />
				<div>
					<StoreButton type="android" />
					<StoreButton type="ios" />
				</div>
				<br />
				{!isLifetime && (
					<>
						<p>{formatMessage(messages.successBodyTwo)}</p>
						<br />
					</>
				)}

				<SubmitButton
					onPress={async () => {
						logEvent({ eventName: 'Redeem : Start Calm Now : Clicked' });
						await routerPush('/app');
					}}
					textColor="white"
				>
					{formatMessage(messages.openApp)}
				</SubmitButton>
			</div>
		);
	};

	const isTeacherGift = () => {
		return giftType === 'teacher';
	};

	const teacherBody = () => {
		return (
			<div className="body">
				<p>
					{formatMessage(messages.teacherSuccessTitle, {
						name: firstNameString(),
						bold: (chunks: ReactNode) => <b>{chunks}</b>,
					})}
				</p>
				<br />
				<p>
					{formatMessage(messages.teacherSuccessBody, {
						teacherlink: (chunks: ReactNode) => (
							<a
								href="http://cdn.calm.com/documents/teachers-onboarding-manual.pdf"
								target="_blank"
								rel="noopener noreferrer"
								onClick={onClickOnboardingDoc}
							>
								{chunks}
							</a>
						),
					})}
				</p>
				<br />
				<p>
					{formatMessage(messages.teacherContactUs, {
						contactlink: (chunks: ReactNode) => <a href={generatePartnerUrl('contact', user)}>{chunks}</a>,
					})}
				</p>
			</div>
		);
	};

	return (
		<Wrapper id="redeem-success">
			{
				// TODO: Maybe here is where I would check to redeem type?
				isTeacherGift() ? teacherBody() : defaultBody()
			}
		</Wrapper>
	);
};

export default RedeemSuccess;
